import { Attribute, Relationship, Resource } from 'json-api';
import { AtomResource } from '../classes/atom-resource';
import { Platform } from './platform.resource';
import { Country } from './country.resource';
import { NumberFilter, StringFilter } from 'src/app/filter/filter';
import { Lang } from './lang.resource';
import { Job } from './job.resource';
import { Ussdnodes, UssdnodesConfig } from './ussd-node.resource';
import {
  CountryConfig,
  EndUserProviderJobConfig,
  LangConfig,
  PlatformConfig,
} from 'src/app/shared/tools/search/search-config';
import { AtomService } from '../atom.service';
import {
  ResourceConfig,
  ResourceConfigOne,
} from 'src/app/resources/resources-interfaces/resource-config';

@Resource({
  type: 'ussdmenus',
})
export class Ussdmenus extends AtomResource {
  @Attribute() name?: string;
  @Attribute({ key: 'external_id' }) externalId?: string;

  @Relationship() enduserproviderjob?: Job;
  @Relationship() country?: Country;
  @Relationship() platform?: Platform;
  @Relationship() langs?: Lang[];
  @Relationship() ussdnodes?: Ussdnodes[];
}
export class UssdmenusConfig implements ResourceConfig {
  tables = {
    includes: [
      'enduserproviderjob.firm.headquarterscountry',
      'country',
      'platform',
      'langs',
      'ussdnodes.parentussdnode',
      'ussdnodes.pack',
    ],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main', translatable: false }],
        header: 'ID',
        translatable: false,
        displayed: true,
      },
      {
        columnDef: 'name',
        displayedDatas: [
          { path: ['name'], class: 'main', translatable: false },
        ],
        header: 'Name',
        translatable: false,
        displayed: true,
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'externalId',
        displayedDatas: [
          { path: ['externalId'], class: 'mat-body-1', translatable: false },
        ],
        header: 'External ID',
        translatable: false,
        displayed: true,
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'enduserproviderjob.firm.name',
        displayedDatas: [
          {
            path: ['enduserproviderjob.firm.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: ['enduserproviderjob.firm.headquarterscountry.name'],
            class: 'mat-body-1',
            translatable: true,
          },
        ],
        header: 'Enduser Provider Job - Firm',
        translatable: true,
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'enduserproviderjob.firm.id',
          icon: 'signal_cellular_alt',
        },
      },
      {
        columnDef: 'country.name',
        displayedDatas: [
          { path: ['country.name'], class: 'mat-body-2', translatable: true },
          { path: ['country.id'], class: 'mat-body-1', translatable: false },
        ],
        header: 'Country',
        translatable: true,
        displayed: true,
        link: {
          resource: 'countries',
          filter: 'country.id',
          icon: 'language',
        },
        options: {
          flag: 'country.id',
        },
      },
      {
        columnDef: 'platform.name',
        displayedDatas: [
          { path: ['platform.name'], class: 'main', translatable: true },
        ],
        header: 'Platform',
        translatable: true,
        displayed: true,
        link: {
          resource: 'platforms',
          filter: 'platform.id',
          icon: 'dns',
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'list',
      title: [
        {
          path: ['name'],
          translatable: false,
        },
      ],
      id: true,
    },
    elements: [
      {
        showEmpty: true,
        list: [
          {
            icon: 'signal_cellular_alt',
            title: 'Enduser Provider Job - Firm',
            APIKey: 'enduserproviderjob',
            value: {
              path: ['enduserproviderjob.firm.name'],
              translatable: true,
            },
            link: {
              resource: 'firms',
              filter: 'enduserproviderjob.firm.id',
            },
          },
          {
            icon: 'language',
            title: 'Country',
            APIKey: 'country',
            value: {
              path: ['country.name'],
              translatable: true,
            },
            link: {
              resource: 'countries',
              filter: 'country.id',
            },
            options: {
              flag: 'country.id',
            },
          },
          {
            icon: 'dns',
            title: 'Platform',
            APIKey: 'platform',
            value: {
              path: ['platform.name'],
              translatable: true,
            },
            link: {
              resource: 'platforms',
              filter: 'platform.id',
            },
          },
          {
            icon: 'password',
            title: 'External ID',
            APIKey: 'externalId',
            value: {
              path: ['externalId'],
              translatable: false,
            },
          },
        ],
      },
      {
        title: 'Tree Nodes',
        tree: ['ussdnodes'],
      },
    ],
  };
  wizard = {
    tabs: [
      {
        title: 'Menu',
        formGroup: 'menu',
        options: {
          type: 'sequential',
          create: true,
        },
        list: [
          {
            type: 'text',
            title: 'Name',
            formControl: 'name',
            translatable: false,
            required: true,
          },
          {
            type: 'text',
            title: 'External ID',
            formControl: 'externalId',
            translatable: false,
            required: false,
          },
          {
            type: 'resource',
            title: 'Country',
            formControl: 'country',
            search: new CountryConfig(this.atom),
            required: true,
            // notEditable: true,
            check: 'equal',
            activatingSequence: [
              {
                group: 'menu',
                control: 'langs',
                noCheckResult: true,
                filters: {
                  filter: 'equal.countries.id',
                },
              },
              {
                group: 'menu',
                control: 'enduserproviderjob',
                filters: {
                  filter: 'equal.firm.headquarterscountry.id',
                  fixFilter: { 'equal.activity.id': '2,3,4' },
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Lang',
            formControl: 'langs',
            search: new LangConfig(this.atom, true),
            // notEditable: true,
            required: true,
            multiple: true,
            check: 'equal',
          },
          {
            type: 'resource',
            title: 'Enduser Povider Job',
            formControl: 'enduserproviderjob',
            search: new EndUserProviderJobConfig(this.atom),
            required: true,
            check: 'equal',
            // activatingSequence: [
            //   {
            //     group: 'menu',
            //     control: 'platform',
            //     filters: {
            //       filter: 'equal.platformcapabilities.enduserproviderjob.id',
            //     },
            //   },
            // ],
          },
          {
            type: 'resource',
            title: 'Platform',
            formControl: 'platform',
            search: new PlatformConfig(this.atom),
            required: true,
            check: 'equal',
          },
        ],
      },
      {
        title: 'Tree Nodes',
        formGroup: 'nodes',
        options: {
          noActions: true,
        },
        list: [
          {
            type: 'tree',
            formControl: 'ussdnodes',
            required: false,
            tree: {
              ussdnodes: UssdnodesConfig,
            },
          },
        ],
      },
    ],
  };
  resource = Ussdmenus;
  resourceName = 'USSD Menu';
  resourceKey = 'ussdmenus';
  includes = [
    'enduserproviderjob.firm.headquarterscountry',
    'enduserproviderjob.activity',
    'country',
    'platform.job.firm',
    'langs',
    'ussdnodes.ussdmenu',
    'ussdnodes.parentussdnode',
    'ussdnodes.pack',
  ];
  filters = [
    new NumberFilter('id', 'ID', true),
    new StringFilter('name', 'Name'),
    new StringFilter('enduserproviderjob.firm.name', 'Enduser Provider Name'),
    new StringFilter('country.id', 'Country ID'),
    new StringFilter('country.name', 'Country Name'),
  ];
  constructor(private atom: AtomService) {}
}
